import { connect, useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dispatch } from 'redux';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';

import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { KnmResultDecisionStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionStatusEnum';
import { IKnmAttachmentDocument } from 'KnmPackage/KnmDocumentsModule/interfaces/IKnmAttachmentDocument';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ErknmPrescribeCancelDocument } from 'KnmPackage/MainModule/components/ErknmPrescribeCancelModal/ErknmPrescribeCancelDocument';
import {
  getDecisionFormRef,
  getDecisionUrlId,
  getErknmDecisionPrescribeIsCancelSignCompleted,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import {
  decisionAddVisibleModal,
  decisionErknmCancelPrescribe,
} from 'DecisionPackage/MainModule/actions/decisionActions';
import { FieldMark } from 'KnmPackage/MainModule/components/FieldMark/FieldMark';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';
import { IPrescribeCancelData } from 'KnmPackage/MainModule/types/IKnmState';
import { getErknmKnmPrescribeDataForCancelSign } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { knmSetSelectedPrescribeCancelData } from 'KnmPackage/MainModule/actions/knmActions';
import { KnmModalsEnum } from 'KnmPackage/MainModule/enums/KnmModalsEnum';

import styles from './DecisionPrescribeCancelModal.module.scss';

interface IDecisionPrescribeCancelModalProps {
  visible: boolean;
  onClose: () => void;
}

interface IDecisionPrescribeCancelModalStoreProps {
  knmId: string | undefined;
  formRef: IDecisionFormRef | undefined;
  prescribeData: IPrescribeCancelData | undefined;
  isCancelSignCompleted?: boolean;
}

interface IForm {
  selectedPrescribe: ISelectOption<string> | undefined;
  comment: string;
  attachment: IKnmAttachmentDocument | undefined;
}

const DecisionPrescribeCancelModalWithoutStore = ({
  visible,
  onClose,
  knmId,
  formRef,
  prescribeData,
  isCancelSignCompleted,
}: IDecisionPrescribeCancelModalProps & IDecisionPrescribeCancelModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const formRefInner = useRef<FormApi<IForm, Partial<IForm>>>();
  const valuesParentForm = formRef?.current.getState().values;
  const [isCanceling, setIsCanceling] = useState(false);

  const validateForm = useCallback((values: IForm) => {
    return {
      selectedPrescribe: makeRequired('Поле не может быть пустым')(values.selectedPrescribe),
      comment: makeRequired('Поле не может быть пустым')(values.comment),
      attachment: makeRequired('Поле не может быть пустым')(values.attachment),
    };
  }, []);

  const handleClose = () => {
    onClose();
    formRefInner?.current?.reset();
  };

  const prescribeOptions = useMemo(() => {
    const prescribes: ISelectOption<string>[] = [];
    const agreements = valuesParentForm?.agreements;

    valuesParentForm?.organization?.resultDecisions?.forEach(decision => {
      if (
        (decision.kind?.code === KindResultDecisionCodesEnum.D_1 ||
          decision.kind?.code === KindResultDecisionCodesEnum.D_7) &&
        (decision.status === KnmResultDecisionStatusEnum.COMPLETED ||
          decision.status === KnmResultDecisionStatusEnum.NOT_COMPLETED) &&
        decision.signed &&
        decision.guid
      ) {
        const bindedAgreement = agreements?.find(
          agreement =>
            agreement.prescription?.value === decision.guid &&
            (agreement.status === AgreementStatusEnum.DRAFT ||
              agreement.status === AgreementStatusEnum.READY_TO_APPROVE ||
              agreement.status === AgreementStatusEnum.ON_APPROVAL ||
              agreement.status === AgreementStatusEnum.APPROVED ||
              agreement.status === AgreementStatusEnum.PENDING_TERMINATION ||
              agreement.status === AgreementStatusEnum.COMPLETED),
        );

        if (!bindedAgreement) {
          prescribes.push({
            id: decision.guid,
            label: `Решение - №${decision.number}`,
            value: decision.guid,
          });
        }
      }
    });

    return prescribes;
  }, [valuesParentForm]);

  const handleCancel = useCallback(
    async (values: IForm) => {
      let data: IPrescribeCancelData = { knmId, ...values, prescriptionGuid: values.selectedPrescribe?.id };

      const isCancelSigned = valuesParentForm?.organization?.resultDecisions?.some(
        o => o.guid === data.prescriptionGuid && o.isCancelSigned,
      );

      if (data.prescriptionGuid) {
        if (isCancelSigned) {
          if (prescribeData && prescribeData.fioSigner && prescribeData.signerPosition?.value) {
            data = { ...data, fioSigner: prescribeData.fioSigner, signerPosition: prescribeData.signerPosition };
          }
          dispatch(decisionErknmCancelPrescribe({ ...data, signerPosition: data.signerPosition?.value }));
        } else {
          dispatch(knmSetSelectedPrescribeCancelData(data));
          dispatch(decisionAddVisibleModal(KnmModalsEnum.ERKNM_SIGN_AFTER_CANCEL));
        }
      }
    },
    [knmId, handleClose],
  );

  useEffect(() => {
    if (formRefInner.current && isCancelSignCompleted && !isCanceling) {
      const innerValues = formRefInner.current.getState().values;
      const prescriptionGuid = innerValues.selectedPrescribe?.id;

      if (prescriptionGuid) {
        const isCancelSigned = valuesParentForm?.organization?.resultDecisions?.some(
          o => o.guid === prescriptionGuid && o.isCancelSigned,
        );

        if (isCancelSigned) {
          let data: IPrescribeCancelData = {
            knmId,
            ...innerValues,
            prescriptionGuid,
          };

          if (prescribeData?.fioSigner && prescribeData.signerPosition?.value) {
            data = {
              ...data,
              fioSigner: prescribeData.fioSigner,
              signerPosition: prescribeData.signerPosition,
            };
          }
          setIsCanceling(true);
          dispatch(decisionErknmCancelPrescribe({ ...data, signerPosition: data.signerPosition?.value }));
        }
      }
    }
  }, [valuesParentForm, prescribeData, isCancelSignCompleted]);

  const renderContentForm = useCallback(
    ({ handleSubmit, form, values }: FormRenderProps<IForm>) => {
      formRefInner.current = form;

      return (
        <>
          <ModalBody size="content">
            <FieldBlock
              label={<FieldMark fieldName="selectedPrescribe" label="Номер предписания" />}
              placementLabel="left"
              color="black"
              className={styles.FieldBlock}
            >
              <SelectInputField
                name="selectedPrescribe"
                className={styles.SelectInput}
                options={prescribeOptions}
                isClearable={false}
              />
            </FieldBlock>
            <FieldBlock
              label={<FieldMark fieldName="comment" label="Комментарий" />}
              placementLabel="left"
              color="black"
              className={styles.FieldBlock}
            >
              <TextareaField
                name={`comment`}
                size="medium"
                resize="vertical"
                inputClassName={styles.CopyReasonTextarea}
                autoHeight={true}
              />
            </FieldBlock>
            <ErknmPrescribeCancelDocument
              values={values.attachment ? [values.attachment] : []}
              addAttachment={data => form.change('attachment', data)}
              removeField={() => form.change('attachment')}
            />
          </ModalBody>
          <ModalActions>
            <Button text="Отменить" size="medium" type="button" onClick={handleClose} />
            <Button text="Сохранить" color="primary" size="medium" type="button" onClick={handleSubmit} />
          </ModalActions>
        </>
      );
    },
    [handleClose],
  );

  return (
    <Modal visible={visible} onClose={handleClose}>
      <ModalHeader title="Отмена предписания" />
      <Form<IForm> render={renderContentForm} onSubmit={handleCancel} validate={validateForm} />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IDecisionPrescribeCancelModalStoreProps => ({
  knmId: getDecisionUrlId(state),
  formRef: getDecisionFormRef(state),
  prescribeData: getErknmKnmPrescribeDataForCancelSign(state),
  isCancelSignCompleted: getErknmDecisionPrescribeIsCancelSignCompleted(state),
});

export const DecisionPrescribeCancelModal = connect(mapStateToProps)(DecisionPrescribeCancelModalWithoutStore);
